import React, { useEffect, useRef, useState } from "react";
import { ListCommanWay, CreateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import Spinner from "utils/Spinner";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button } from "@heroui/react";

const CashInvoice: React.FC<any> = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [totals, setTotals] = useState<any>({});
  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [sortStat, setSortStat] = useState<any>({});

  // Selected AWB IDs set for checkbox
  const [selectedAwbIds, setSelectedAwbIds] = useState<Set<number>>(new Set());
  // The single selected booking ID (auto-group)
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

  // Generated invoices list for the bottom section (current session)
  const [generatedInvoices, setGeneratedInvoices] = useState<any[]>([]);

  // Pending IRN invoices fetched from backend
  const [pendingIRNInvoices, setPendingIRNInvoices] = useState<any[]>([]);

  const [query, setQuery] = useState<any>({
    date: {
      startDate: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    },
  });

  const [branchOpt, setBranchOpt] = useState<any>();
  const [branchOptions, setBranchOptions] = useState<any>([{ value: 0, label: "Loading..." }]);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : {};
  })();
  const [userDetails] = useState<any>(userD?.state?.user || {});

  // Fetch subbranches
  useQuery(
    ["BranchesListApi_CashInv"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: p.id, label: p.branch_name });
          });
          setBranchOptions(opt);
        }
      },
    }
  );

  // Fetch invoices with pending IRN or proforma NA in a single query
  const { isFetching: pendingIRNFetching, refetch: refetchPendingIRN } = useQuery(
    ["admin_pending_irn_invoices"],
    () =>
      ListCommanWay(
        "/admin/inovic_for_c_b/list",
        { $or: [{ irn_status: "PENDING" }, { irn_status: "NA", is_proforma: true }], isActive: 1 },
        { include: ["_customerId", "_subbranch"], paginate: 1000 }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          const rows = (data.data.data || []).map((r: any) => ({
            ...r,
            _fromProforma: r.irn_status === "NA" && r.is_proforma,
          }));
          setPendingIRNInvoices(rows);
        } else {
          setPendingIRNInvoices([]);
        }
      },
      onError: () => {
        setPendingIRNInvoices([]);
      },
    }
  );

  // Fetch AWB data (same API as CashReport)
  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_cash_invoice_awb_list"],
    () => ListCommanWay("/admin/awbdata/cashreport", { ...query }, {}),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setTableData(data.data.rows);
          setSortStat({});

          let ary = [...data.data.rows];
          let ttt: any = {
            base: 0, cgst: 0, sgst: 0, igst: 0, utgst: 0, grand: 0,
            ten_30: 0, holiday: 0, fuel: 0, chc: 0, weight: 0,
          };
          ary.forEach((a: any) => {
            if (a.isActive) {
              ttt.base += Number(a.base_value || 0);
              ttt.cgst += Number((a.base_value * a.cgst) / 100);
              ttt.sgst += Number((a.base_value * a.sgst) / 100);
              ttt.igst += Number((a.base_value * a.igst) / 100);
              ttt.utgst += Number((a.base_value * (a.utgst || 0)) / 100);
              ttt.grand += Number(a.base_value + a.tax);
              ttt.ten_30 += Number(a.ten_30Rate || 0);
              ttt.holiday += Number(a.holidayRate || 0);
              ttt.fuel += Number(a.fuelRate || 0);
              ttt.chc += Number(a.chcRate || 0);
              ttt.weight += Number(a.weight || 0);
            }
          });
          setTotals(ttt);
        } else {
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
      },
    }
  );

  useEffect(() => {
    setTotals({ grand: 0 });
    refetch();
  }, [query]);

  const __handleSubmit = async () => {
    setSelectedAwbIds(new Set());
    setSelectedBookingId(null);
    setQuery({
      date: {
        startDate: startDate ? startDate : moment().startOf("day").subtract(7, "days").format("YYYY-MM-DD HH:mm:ss"),
        endDate: endDate ? endDate : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      },
      subbranch: branchOpt?.value,
    });
  };

  // Auto-group selection: clicking any AWB selects/deselects entire booking group
  const handleAwbSelect = (awb: any) => {
    const bookingId = awb.assign_to_booking;
    if (!bookingId) {
      UseToast("AWB has no booking ID", "error");
      return;
    }

    if (selectedBookingId === bookingId) {
      // Deselect the group
      setSelectedAwbIds(new Set());
      setSelectedBookingId(null);
    } else {
      // Select new booking group (replacing any previous selection)
      const groupAwbIds = new Set<number>();
      tableData.forEach((r: any) => {
        if (r.assign_to_booking === bookingId && r.isActive && !r._invoice_no_c_b) {
          groupAwbIds.add(r.id);
        }
      });
      setSelectedAwbIds(groupAwbIds);
      setSelectedBookingId(bookingId);
    }
  };

  // Generate Cash Invoice mutation
  const { mutate: mutateGenerateCashInvoice, isLoading: cashInvLoading } = useMutation(
    (bookingIds: number[]) =>
      CreateCommanWay({
        url: "/admin/booking/generateCashInvoice",
        data: { booking_ids: bookingIds },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          const inv = data.data;
          setGeneratedInvoices((prev) => [inv, ...prev]);
          setSelectedAwbIds(new Set());
          setSelectedBookingId(null);

          Swal.fire({
            title: "Cash Invoice Generated",
            html: `
              <div style="text-align: left;">
                <p><strong>Invoice No:</strong> ${inv.invoice_no}</p>
                <p><strong>AWBs Invoiced:</strong> ${inv.awb_count}</p>
                <p><strong>IRN Status:</strong> <span style="color: ${inv.irn_status === "ACTIVE" ? "green" : "#b8860b"}">${inv.irn_status}</span></p>
                ${inv.pdfPath ? `<p><a href="${BASE_URL}${inv.pdfPath}" target="_blank" class="text-primary underline">Download Invoice PDF</a></p>` : ""}
              </div>
            `,
            icon: "success",
            showConfirmButton: true,
            confirmButtonText: inv.pdfPath ? "Download PDF" : "OK",
            showCancelButton: true,
            cancelButtonText: "Close",
            preConfirm: () => {
              if (inv.pdfPath) window.open(BASE_URL + inv.pdfPath, "_blank");
            },
          });

          refetch();
        } else {
          UseToast(data.message || "Failed to generate invoice", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Retry IRN mutation
  const { mutate: mutateRetryIRN, isLoading: retryIRNLoading } = useMutation(
    (invoiceIds: number[]) =>
      CreateCommanWay({
        url: "/admin/booking/retryIRN",
        data: { invoice_ids: invoiceIds },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          const result = data.data;
          if (result.success?.length > 0) {
            UseToast(`IRN activated for ${result.success.length} invoice(s)`, "success");
            // Update generated invoices state
            setGeneratedInvoices((prev) =>
              prev.map((inv) => {
                const s = result.success.find((x: any) => x.id === inv.invoiceId);
                return s ? { ...inv, irn_status: s.irn_status } : inv;
              })
            );
            refetchPendingIRN();
          }
          if (result.failed?.length > 0) {
            result.failed.forEach((f: any) => {
              UseToast(`IRN failed for invoice ${f.id}: ${f.error}${f.portion ? ` (${f.portion})` : ""}`, "error");
            });
          }
        } else {
          UseToast(data.message || "Retry failed", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const handleGenerateCashInvoice = () => {
    if (!selectedBookingId) {
      UseToast("Please select AWBs from a booking first", "error");
      return;
    }
    Swal.fire({
      title: "Generate Cash Invoice?",
      html: `<p>This will generate a cash invoice for <strong>${selectedAwbIds.size}</strong> AWB(s) from booking <strong>#${selectedBookingId}</strong>.</p>`,
      icon: "question",
      confirmButtonText: "Generate",
      showCancelButton: true,
    }).then((x) => {
      if (x.isConfirmed) {
        mutateGenerateCashInvoice([selectedBookingId]);
      }
    });
  };

  // Sort helpers (same as CashReport)
  const GET_SORT_ICON = (key: string) => {
    const handler = () => handleSort(key);
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block cursor-pointer" onClick={handler} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block cursor-pointer" onClick={handler} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block cursor-pointer" onClick={handler} />;
    }
  };

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";
    let sortedData = data.sort((a: any, b: any) => {
      const valueA = key.split(".").reduce((obj: any, k: string) => obj?.[k], a);
      const valueB = key.split(".").reduce((obj: any, k: string) => obj?.[k], b);
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });
    setSortStat({ ...sortStat, [key]: order });
    setTableData(sortedData);
  };

  // Filter: un-invoiced AWBs only
  const uninvoicedData = tableData.filter((r: any) => r.isActive && !r._invoice_no_c_b);

  // Group uninvoiced AWBs by booking ID into pseudo-rows for the merged table
  const uninvoicedBookingGroups = React.useMemo(() => {
    const groups: Record<number, any> = {};
    uninvoicedData.forEach((r: any) => {
      const bid = r.assign_to_booking;
      if (!bid) return;
      if (!groups[bid]) {
        groups[bid] = {
          _isUninvoiced: true,
          bookingId: bid,
          awb_count: 0,
          awb_nos: [] as string[],
          total_value: 0,
          shipper: r._assign_to_booking?.shipper_sh || "-",
          branch: r._assign_to_booking?.shipper_city || "-",
          date: r._assign_to_booking?.date,
        };
      }
      groups[bid].awb_count += 1;
      if (r.awb_no) groups[bid].awb_nos.push(r.awb_no);
      groups[bid].total_value += Number(r.base_value || 0) + Number(r.tax || 0);
    });
    return Object.values(groups);
  }, [uninvoicedData]);

  // Merged rows: uninvoiced bookings + pending IRN + proforma
  const mergedPendingRows = [...uninvoicedBookingGroups, ...pendingIRNInvoices];

  return (
    <>
      {(isLoading || isFetching || cashInvLoading || retryIRNLoading || pendingIRNFetching) && <Spinner />}

      <div className="flex items-center justify-center">
        <h1 className="text-center my-2 text-lg font-semibold mr-4">Cash Invoice</h1>
        <div>
          TOTAL (Un-invoiced):{" "}
          <span style={{ fontWeight: 600, color: "red" }}>
            {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
              uninvoicedData.reduce((s: number, r: any) => s + Number(r.base_value + r.tax), 0)
            )}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 border-[1px] p-2 rounded-md">
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-x-4 gap-y-2">
            {(userDetails.role_name == 1 || userDetails.role_name == 8 || userDetails.role_name == 13) && (
              <div className="items-center gap-2">
                <label className="mb-2 text-sm font-semibold text-gray-900">Subbranch</label>
                <Select
                  className="w-48 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
                  options={branchOptions}
                  onChange={(e: any) => setBranchOpt(e)}
                  value={branchOpt}
                  isClearable={true}
                />
              </div>
            )}

            <AfDateRangePicker setStartDate={setStartDate} setEndDate={setEndDate} label="Date Range" />

            <Button type="button" className="ml-3 font-semibold font-inter text-white" radius="sm" color="success" onPress={__handleSubmit}>
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* ═══════════ MERGED TABLE: Uninvoiced Bookings + Pending IRN + Proforma ═══════════ */}
      <div className="px-4 mt-4">
        <h2 className="text-md font-semibold mb-2">
          Invoice Actions ({mergedPendingRows.length})
          {uninvoicedBookingGroups.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              — {uninvoicedBookingGroups.length} uninvoiced booking(s),{" "}
              {pendingIRNInvoices.length} pending/proforma invoice(s)
            </span>
          )}
        </h2>
        {mergedPendingRows.length > 0 ? (
          <table className="w-full mb-4" style={{ fontSize: "85%" }}>
            <thead className="bg-yellow-100">
              <tr>
                <th className="border border-slate-200 px-2 py-1">Sr.</th>
                <th className="border border-slate-200 px-2 py-1">Type</th>
                <th className="border border-slate-200 px-2 py-1">Invoice / Booking</th>
                <th className="border border-slate-200 px-2 py-1">Customer / Shipper</th>
                <th className="border border-slate-200 px-2 py-1">Branch</th>
                <th className="border border-slate-200 px-2 py-1">Date</th>
                <th className="border border-slate-200 px-2 py-1">AWBs</th>
                <th className="border border-slate-200 px-2 py-1 text-right">Total Value</th>
                <th className="border border-slate-200 px-2 py-1">Status</th>
                <th className="border border-slate-200 px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mergedPendingRows.map((r: any, i: number) => {
                if (r._isUninvoiced) {
                  // Uninvoiced booking group row
                  return (
                    <tr key={"uninv" + r.bookingId} className="hover:bg-blue-50">
                      <td className="border border-slate-200 px-2 text-center">{i + 1}</td>
                      <td className="border border-slate-200 px-2 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-200 text-blue-800">NEW</span>
                      </td>
                      <td className="border border-slate-200 px-2 text-center font-mono font-semibold">
                        {r.awb_nos.join(", ")}
                      </td>
                      <td className="border border-slate-200 px-2">{r.shipper}</td>
                      <td className="border border-slate-200 px-2 text-center">{r.branch}</td>
                      <td className="border border-slate-200 px-2 text-center">
                        {r.date ? moment(r.date, "DD-MM-YYYY HH:mm:ss").format("DD/MM/YYYY") : "-"}
                      </td>
                      <td className="border border-slate-200 px-2 text-center">{r.awb_count}</td>
                      <td className="border border-slate-200 px-2 pr-4 text-right font-semibold">
                        {Number(r.total_value || 0).toFixed(2)}
                      </td>
                      <td className="border border-slate-200 px-2 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">NO INVOICE</span>
                      </td>
                      <td className="border border-slate-200 px-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => {
                            Swal.fire({
                              title: "Generate Cash Invoice?",
                              html: `<p>This will generate a cash invoice for <strong>${r.awb_count}</strong> AWB(s):<br/><strong>${r.awb_nos.join(", ")}</strong></p>`,
                              icon: "question",
                              confirmButtonText: "Generate",
                              showCancelButton: true,
                            }).then((x) => {
                              if (x.isConfirmed) mutateGenerateCashInvoice([r.bookingId]);
                            });
                          }}
                          className="px-2 py-0.5 bg-emerald-600 text-white rounded-md text-xs"
                        >
                          Generate Invoice
                        </button>
                      </td>
                    </tr>
                  );
                }
                // Pending IRN / Proforma invoice row
                return (
                  <tr key={"pirn" + r.id} className="hover:bg-yellow-50">
                    <td className="border border-slate-200 px-2 text-center">{i + 1}</td>
                    <td className="border border-slate-200 px-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        r._fromProforma ? "bg-orange-200 text-orange-800" : "bg-yellow-200 text-yellow-800"
                      }`}>{r._fromProforma ? "PROFORMA" : "IRN"}</span>
                    </td>
                    <td className="border border-slate-200 px-2 text-center font-mono font-semibold">
                      {r._subbranch?.branch_code}{r.invoice_no}
                    </td>
                    <td className="border border-slate-200 px-2">
                      {r._customerId ? `${r._customerId.code} - ${r._customerId.sh}` : " ::: Cash ::: " + r.shipper_sh}
                    </td>
                    <td className="border border-slate-200 px-2 text-center">{r._subbranch?.branch_name || "-"}</td>
                    <td className="border border-slate-200 px-2 text-center">
                      {r.invDate ? moment(r.invDate).format("DD/MM/YYYY") : "-"}
                    </td>
                    <td className="border border-slate-200 px-2 text-center">-</td>
                    <td className="border border-slate-200 px-2 pr-4 text-right font-semibold">
                      {Number(r.total_value || 0).toFixed(2)}
                    </td>
                    <td className="border border-slate-200 px-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        r._fromProforma ? "bg-orange-200 text-orange-800" : "bg-yellow-200 text-yellow-800"
                      }`}>{r._fromProforma ? "PROFORMA" : "PENDING"}</span>
                    </td>
                    <td className="border border-slate-200 px-2 text-center whitespace-nowrap">
                      {r._subbranch?.branch_code && (
                        <a
                          href={`${BASE_URL}/invoices_c/${r._subbranch.branch_code}/${r._subbranch.branch_code}${r.invoice_no}.pdf`}
                          target="_blank"
                          className="text-white bg-red-900 px-2 py-0.5 rounded-md text-xs mr-1"
                        >
                          PDF
                        </a>
                      )}
                      <button
                        onClick={() => mutateRetryIRN([r.id])}
                        className={`px-2 py-0.5 text-white rounded-md text-xs ${r._fromProforma ? "bg-emerald-600" : "bg-yellow-600"}`}
                      >
                        {r._fromProforma ? "Generate Invoice" : "Retry IRN"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-500 mb-4">No pending actions found.</p>
        )}
      </div>

      {/* Recently Generated Invoices (current session) */}
      {generatedInvoices.length > 0 && (
        <div className="px-4 mt-4">
          <h2 className="text-md font-semibold mb-2">Recently Generated Invoices (this session)</h2>
          <table className="w-full mb-8" style={{ fontSize: "85%" }}>
            <thead className="bg-gray-200">
              <tr>
                <th className="border border-slate-200 px-2 py-1">Invoice ID</th>
                <th className="border border-slate-200 px-2 py-1">Invoice No</th>
                <th className="border border-slate-200 px-2 py-1">AWB Count</th>
                <th className="border border-slate-200 px-2 py-1">IRN Status</th>
                <th className="border border-slate-200 px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {generatedInvoices.map((inv: any, i: number) => (
                <tr key={"gi" + i} className="hover:bg-gray-50">
                  <td className="border border-slate-200 px-2 text-center">{inv.invoiceId}</td>
                  <td className="border border-slate-200 px-2 text-center font-mono">{inv.invoice_no}</td>
                  <td className="border border-slate-200 px-2 text-center">{inv.awb_count}</td>
                  <td className="border border-slate-200 px-2 text-center">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        inv.irn_status === "ACTIVE"
                          ? "bg-green-200 text-green-800"
                          : inv.irn_status === "PENDING"
                          ? "bg-yellow-200 text-yellow-800"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {inv.irn_status || "N/A"}
                    </span>
                  </td>
                  <td className="border border-slate-200 px-2 text-center whitespace-nowrap">
                    {inv.pdfPath && (
                      <a
                        href={BASE_URL + inv.pdfPath}
                        target="_blank"
                        className="text-white bg-red-900 px-2 py-0.5 rounded-md text-xs mr-1"
                      >
                        PDF
                      </a>
                    )}
                    {inv.irn_status === "PENDING" && (
                      <button
                        onClick={() => mutateRetryIRN([inv.invoiceId])}
                        className="px-2 py-0.5 bg-yellow-600 text-white rounded-md text-xs"
                      >
                        Retry IRN
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default CashInvoice;
